import React, { useEffect } from 'react';
import { ZODIAC_SIGNS } from '../../data/zodiacSigns';
import { LANGUAGES } from '../../data/languages';
import { COUNTRIES } from '../../data/countries';
import './WriterProfileModal.css';

const getCountryInfo = (country) => {
  if (!country) return null;
  const found = COUNTRIES.find((c) => c.name === country);
  return found || null;
};

const getZodiacInfo = (zodiacId) => {
  if (!zodiacId) return null;
  return ZODIAC_SIGNS.find(
    (z) => z.id === zodiacId || z.name === zodiacId
  ) || null;
};

const getLanguageInfo = (langCode) => {
  if (!langCode) return null;
  return LANGUAGES.find(
    (l) => l.code === langCode || l.name === langCode
  ) || null;
};

export default function WriterProfileModal({
  isOpen,
  profile,
  loading,
  onClose,
  onSendMessage,
  totalMessages = 0,
}) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  const zodiac = profile ? getZodiacInfo(profile.zodiac_sign) : null;
  const country = profile ? getCountryInfo(profile.country) : null;
  const languages = profile?.languages
    ? profile.languages.map(getLanguageInfo).filter(Boolean)
    : [];

  return (
    <div className="writer-profile-overlay" onClick={handleOverlayClick}>
      <div className="writer-profile-modal">
        {loading || !profile ? (
          <div className="writer-profile-loading">
            <div className="writer-profile-spinner" />
            <span>Loading profile...</span>
          </div>
        ) : (
          <>
            <button
              className="writer-profile-close"
              onClick={onClose}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Avatar */}
            <div className="writer-profile-avatar-section">
              <div className="writer-profile-avatar">
                {profile.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={profile.handle}
                    className="writer-profile-avatar-img"
                  />
                ) : (
                  <span className="writer-profile-avatar-fallback">&#10024;</span>
                )}
              </div>
              <h2 className="writer-profile-handle">@{profile.handle}</h2>
            </div>

            {/* Details */}
            <div className="writer-profile-details">
              {country && (
                <div className="writer-profile-detail-row">
                  <span className="writer-profile-detail-icon">{country.flag}</span>
                  <span className="writer-profile-detail-text">{country.name}</span>
                </div>
              )}

              {zodiac && (
                <div className="writer-profile-detail-row">
                  <span className="writer-profile-detail-icon">{zodiac.emoji}</span>
                  <span className="writer-profile-detail-text">
                    {zodiac.name}
                    <span className="writer-profile-zodiac-dates">{zodiac.dates}</span>
                  </span>
                </div>
              )}
            </div>

            {/* Bio */}
            {profile.bio && (
              <div className="writer-profile-bio">
                <p>"{profile.bio}"</p>
              </div>
            )}

            {/* Languages */}
            {languages.length > 0 && (
              <div className="writer-profile-languages">
                <span className="writer-profile-languages-label">Speaks</span>
                <div className="writer-profile-language-chips">
                  {languages.map((lang) => (
                    <span key={lang.code} className="writer-profile-language-chip">
                      {lang.flag} {lang.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Send message button */}
            <button
              className="writer-profile-send-btn"
              onClick={onSendMessage}
              disabled={totalMessages <= 0}
            >
              <span>&#128140;</span>
              <span>Send a Letter</span>
              <span className="writer-profile-msg-count">({totalMessages})</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
